import React, { useEffect, useState } from "react";
import { Drawer, Tag, Progress, Descriptions, Card, Typography, Spin, message, Space } from "antd";
import { getEpisode, riskColor, parseTimecodes, JudgeResultRow } from "./api";

interface Props {
  open: boolean;
  refId: string | null;
  /** When present, the drawer shows the AI assessment. */
  result?: JudgeResultRow | null;
  onClose: () => void;
}

const EpisodeDrawer: React.FC<Props> = ({ open, refId, result, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (open && refId) {
      setLoading(true);
      getEpisode(refId)
        .then(setData)
        .catch(() => message.error("Failed to load episode details"))
        .finally(() => setLoading(false));
    } else {
      setData(null);
    }
  }, [open, refId]);

  const meta = data?.meta || {};
  const chunks: any[] = data?.chunks || [];
  const flaggedTimecodes = parseTimecodes(result?.TIMECODES);

  return (
    <Drawer
      width={640}
      open={open}
      onClose={onClose}
      title={
        <Space>
          <span>{meta.TITLE || result?.TITLE || refId}</span>
          {result && <Tag color={riskColor(result.RISK_LEVEL)}>{(result.RISK_LEVEL || "").toUpperCase()}</Tag>}
        </Space>
      }
    >
      {loading ? (
        <Spin />
      ) : (
        <>
          <Card size="small" title="Episode info" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="REF ID">{refId}</Descriptions.Item>
            <Descriptions.Item label="Title">{meta.TITLE ?? "-"}</Descriptions.Item>
              <Descriptions.Item label="Full description">{meta.DESCRIPTION_FULL ?? "-"}</Descriptions.Item>
              <Descriptions.Item label="Description (250)">{meta.DESCRIPTION_250 ?? "-"}</Descriptions.Item>
              <Descriptions.Item label="Description (100)">{meta.DESCRIPTION_100 ?? "-"}</Descriptions.Item>
              <Descriptions.Item label="Caption status">
                {meta.CHUNK_SKIP_REASON ? (
                  <Tag color="red">{meta.CHUNK_SKIP_REASON}</Tag>
                ) : chunks.length > 0 ? (
                  <Tag color="green">Chunked ({chunks.length})</Tag>
                ) : (
                  <Tag>Not processed</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {result && (
            <Card size="small" title="AI assessment" style={{ marginBottom: 16 }}>
              <Progress percent={Number(result.SCORE) || 0} />
              <Typography.Paragraph style={{ marginTop: 8 }}>{result.REASON}</Typography.Paragraph>
              {flaggedTimecodes.length > 0 && (
                <Space wrap>
                  {flaggedTimecodes.map((tc) => (
                    <Tag key={tc} color="orange">
                      {tc}
                    </Tag>
                  ))}
                </Space>
              )}
            </Card>
          )}

          <Card size="small" title={`Matching caption chunks (${chunks.length})`} style={{ marginBottom: 16 }}>
            {chunks.length === 0 ? (
              <Typography.Text type="secondary">
                {meta.CHUNK_SKIP_REASON
                  ? `No caption chunks — ${meta.CHUNK_SKIP_REASON}`
                  : "No caption chunks for this episode."}
              </Typography.Text>
            ) : (
              chunks.map((c) => (
                <div key={c.ID} style={{ marginBottom: 12 }}>
                  <Typography.Text strong style={{ color: "#d48806" }}>
                    {c.TIMECODE_START}
                  </Typography.Text>
                  <div>{c.CHUNK_TEXT}</div>
                </div>
              ))
            )}
          </Card>
        </>
      )}
    </Drawer>
  );
};

export default EpisodeDrawer;
